import { Flex } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { BerryLoader } from '@web-app/components/BerryLoader';
import { prefix } from '@web-app/constants/growthbook';
import { useGetUserOrganizationData } from '@web-app/hooks/queries/user';
import {
  getDomainFromWindow,
  getOrganizationConfig,
  getOrganizationIdByOrigin,
  WhiteLabelOrgs,
} from '@web-app/utils/api/whitelabel/organization-configs';
import Head from 'next/head';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export interface OrganizationContextProps {
  organizationId?: number;
  isLoadingOrganization: boolean;
}

export const OrganizationContext = createContext<OrganizationContextProps>({
  isLoadingOrganization: true,
});

/**
 * Context to track when GrowthBook attributes have been fully set.
 * This includes both user attributes (from IdentifyGrowthBookUser) and
 * organization attributes (from OrganizationProvider).
 * This provides a reliable way to detect when all attributes are ready.
 */
const GrowthBookAttributesContext = createContext<{
  attributesSet: boolean;
}>({
  attributesSet: false,
});

export const useGrowthBookAttributesSet = () => {
  const context = useContext(GrowthBookAttributesContext);
  if (!context) {
    throw new Error(
      'useGrowthBookAttributesSet must be used within OrganizationProvider',
    );
  }
  return context.attributesSet;
};

type OrganizationProviderProps = React.PropsWithChildren;

// Module-level variable - persists across component remounts (client-side only)
let hasEverLoadedOrgData = false;

export const OrganizationProvider = ({
  children,
}: OrganizationProviderProps) => {
  const gb = useGrowthBook();
  const { data: orgData, isPending: isLoadingOrg } =
    useGetUserOrganizationData();
  const [attributesSet, setAttributesSet] = useState(false);

  // Track if org data has ever been loaded (client-side only to avoid SSR issues)
  if (typeof window !== 'undefined' && orgData && !isLoadingOrg) {
    hasEverLoadedOrgData = true;
  }

  const faviconFileName = useMemo(() => {
    return process.env.NEXT_PUBLIC_APP_ENV === 'production'
      ? 'favicon'
      : 'favicon-staging';
  }, []);

  useEffect(() => {
    if (!orgData) return;

    // Skip redirect in non-production environments to allow QA/testing
    const isProduction = process.env.NEXT_PUBLIC_APP_ENV === 'production';

    if (
      isProduction &&
      orgData.organizationId === WhiteLabelOrgs.NUTRISENSE &&
      getOrganizationIdByOrigin(getDomainFromWindow()) !==
        WhiteLabelOrgs.NUTRISENSE &&
      !window.location.host.includes('localhost')
    ) {
      const config = getOrganizationConfig(WhiteLabelOrgs.NUTRISENSE);
      window.location.replace(
        `https://${config.branding.origin}${window.location.pathname}`,
      );
    }
  }, [orgData]);

  useEffect(() => {
    if (!gb) return;

    // Wait for the organization query to finish loading before setting attributes
    // This ensures we have the final organizationId value (or undefined if no room)
    if (isLoadingOrg) return;

    const attributes = { ...gb.getAttributes() };
    const providerOrganizationId = orgData?.providerOrganizationId;
    if (typeof providerOrganizationId === 'number') {
      attributes.organizationId = providerOrganizationId;
    } else {
      delete attributes.organizationId;
    }
    gb.setAttributes(attributes);
    datadogLogs.logger.info('GrowthBook organization attributes updated', {
      prefix,
      updatedAttributes: attributes,
    });

    // Mark attributes as fully set after organization attributes are updated
    // This happens after user attributes are set by IdentifyGrowthBookUser
    setAttributesSet(true);
  }, [gb, orgData, isLoadingOrg]);

  useEffect(() => {
    if (isLoadingOrg) return;
    if (orgData) {
      datadogLogs.setGlobalContextProperty(
        'organizationId',
        orgData.organizationId,
      );
      datadogRum.setGlobalContextProperty(
        'organizationId',
        orgData.organizationId,
      );
    } else {
      datadogLogs.removeGlobalContextProperty('organizationId');
      datadogRum.removeGlobalContextProperty('organizationId');
    }
  }, [isLoadingOrg, orgData]);

  const value = useMemo(() => {
    return {
      organizationId: orgData?.organizationId ?? undefined,
      isLoadingOrganization: isLoadingOrg,
    };
  }, [orgData?.organizationId, isLoadingOrg]);

  // Only show full-screen loader on FIRST load, not on subsequent navigations
  const showFullScreenLoader =
    !hasEverLoadedOrgData && (!orgData || isLoadingOrg);

  if (showFullScreenLoader) {
    return (
      <GrowthBookAttributesContext.Provider value={{ attributesSet }}>
        <Head>
          <link
            rel="icon"
            type="image/x-icon"
            href={`/${faviconFileName}.ico`}
          />
          <link
            rel="apple-touch-icon"
            href={`/${faviconFileName}-apple-touch.png.ico`}
            sizes="180x180"
          />
        </Head>
        <Flex
          width="100%"
          height="100vh"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <BerryLoader />
        </Flex>
      </GrowthBookAttributesContext.Provider>
    );
  }

  const organizationId = orgData?.organizationId;

  return (
    <GrowthBookAttributesContext.Provider value={{ attributesSet }}>
      <OrganizationContext.Provider value={value}>
        {organizationId && <Favicon organizationId={organizationId} />}
        {children}
      </OrganizationContext.Provider>
    </GrowthBookAttributesContext.Provider>
  );
};

const Favicon = ({ organizationId }: { organizationId: number }) => {
  const config = getOrganizationConfig(organizationId);
  const { favicon } = config.branding;

  return (
    <Head>
      <link rel="icon" type="image/x-icon" href={favicon.path} />
      <link
        rel="apple-touch-icon"
        href={favicon.appleTouchPath}
        sizes="180x180"
      />
    </Head>
  );
};
